/**
 * Validadores reutilizables para formularios
 */

export type Validator = (value: string | number) => string | null;

/**
 * Validador que requiere que el campo no esté vacío
 */
export const required = (message: string = 'Este campo es obligatorio'): Validator => {
  return (value: string | number) => {
    if (value === '' || value === null || value === undefined) {
      return message;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return message;
    }
    return null;
  };
};

/**
 * Validador para códigos (sin espacios, mayúsculas)
 */
export const codeValidator = (message: string = 'El código no puede contener espacios'): Validator => {
  return (value: string | number) => {
    if (typeof value === 'string' && value.includes(' ')) {
      return message;
    }
    return null;
  };
};

/**
 * Validador para números positivos
 */
export const positiveNumber = (message: string = 'Debe ser un número positivo'): Validator => {
  return (value: string | number) => {
    const num = typeof value === 'number' ? value : parseFloat(value as string);
    if (isNaN(num) || num < 0) {
      return message;
    }
    return null;
  };
};

/**
 * Validador para números mayores que un mínimo
 */
export const minNumber = (min: number, message?: string): Validator => {
  return (value: string | number) => {
    const num = typeof value === 'number' ? value : parseFloat(value as string);
    if (isNaN(num) || num < min) {
      return message || `Debe ser mayor o igual a ${min}`;
    }
    return null;
  };
};

/**
 * Validador para números menores que un máximo
 */
export const maxNumber = (max: number, message?: string): Validator => {
  return (value: string | number) => {
    const num = typeof value === 'number' ? value : parseFloat(value as string);
    if (isNaN(num) || num > max) {
      return message || `Debe ser menor o igual a ${max}`;
    }
    return null;
  };
};

/**
 * Validador para rangos de números
 */
export const numberRange = (min: number, max: number, message?: string): Validator => {
  return (value: string | number) => {
    const num = typeof value === 'number' ? value : parseFloat(value as string);
    if (isNaN(num) || num < min || num > max) {
      return message || `Debe estar entre ${min} y ${max}`;
    }
    return null;
  };
};

/**
 * Validador para longitudes mínimas
 */
export const minLength = (min: number, message?: string): Validator => {
  return (value: string | number) => {
    const str = String(value);
    if (str.length < min) {
      return message || `Debe tener al menos ${min} caracteres`;
    }
    return null;
  };
};

/**
 * Validador para longitudes máximas
 */
export const maxLength = (max: number, message?: string): Validator => {
  return (value: string | number) => {
    const str = String(value);
    if (str.length > max) {
      return message || `No puede tener más de ${max} caracteres`;
    }
    return null;
  };
};

/**
 * Validador para formato de email
 */
export const email = (message: string = 'Formato de email inválido'): Validator => {
  return (value: string | number) => {
    const str = String(value);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (str && !emailRegex.test(str)) {
      return message;
    }
    return null;
  };
};

/**
 * Validador para códigos de pieza (formato específico)
 */
export const pieceCodeValidator = (): Validator => {
  return (value: string | number) => {
    const str = String(value).trim().toUpperCase();
    if (!str) {
      return 'El código es obligatorio';
    }
    if (str.includes(' ')) {
      return 'El código no puede contener espacios';
    }
    if (str.length < 3) {
      return 'El código debe tener al menos 3 caracteres';
    }
    if (str.length > 50) {
      return 'El código no puede tener más de 50 caracteres';
    }
    return null;
  };
};

/**
 * Validador para códigos de piedra
 */
export const stoneCodeValidator = (): Validator => {
  return (value: string | number) => {
    const str = String(value).trim().toUpperCase();
    if (!str) {
      return 'El código es obligatorio';
    }
    if (str.includes(' ')) {
      return 'El código no puede contener espacios';
    }
    if (str.length < 2) {
      return 'El código debe tener al menos 2 caracteres';
    }
    return null;
  };
};

/**
 * Validador para nombres (requiere al menos 2 caracteres)
 */
export const nameValidator = (message: string = 'El nombre debe tener al menos 2 caracteres'): Validator => {
  return (value: string | number) => {
    const str = String(value).trim();
    if (!str) {
      return 'Este campo es obligatorio';
    }
    if (str.length < 2) {
      return message;
    }
    return null;
  };
};

/**
 * Validador para tamaños en mm (0.1 a 1000)
 */
export const sizeMMValidator = (): Validator => {
  return (value: string | number) => {
    const num = typeof value === 'number' ? value : parseFloat(value as string);
    if (value === '' || value === null || value === undefined) {
      return null; // Opcional
    }
    if (isNaN(num)) {
      return 'Debe ser un número válido';
    }
    if (num < 0.1) {
      return 'El tamaño debe ser al menos 0.1 mm';
    }
    if (num > 1000) {
      return 'El tamaño no puede ser mayor a 1000 mm';
    }
    return null;
  };
};

/**
 * Validador para costes (números positivos con 2 decimales)
 */
export const costValidator = (): Validator => {
  return (value: string | number) => {
    if (value === '' || value === null || value === undefined) {
      return null; // Opcional
    }
    const num = typeof value === 'number' ? value : parseFloat(value as string);
    if (isNaN(num)) {
      return 'Debe ser un número válido';
    }
    if (num < 0) {
      return 'El coste no puede ser negativo';
    }
    return null;
  };
};

/**
 * Validador para pesos (números positivos con 3 decimales)
 */
export const weightValidator = (): Validator => {
  return (value: string | number) => {
    if (value === '' || value === null || value === undefined) {
      return null; // Opcional
    }
    const num = typeof value === 'number' ? value : parseFloat(value as string);
    if (isNaN(num)) {
      return 'Debe ser un número válido';
    }
    if (num < 0) {
      return 'El peso no puede ser negativo';
    }
    if (num > 10000) {
      return 'El peso no puede ser mayor a 10000 g';
    }
    return null;
  };
};

/**
 * Combinar múltiples validadores
 */
export const combineValidators = (...validators: Validator[]): Validator => {
  return (value: string | number) => {
    for (const validator of validators) {
      const error = validator(value);
      if (error) {
        return error;
      }
    }
    return null;
  };
};

